import React, { useEffect, useMemo, useState } from 'react';
import { Check, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../../../../../../../../components/ui/button';
import { Card, CardContent } from '../../../../../../../../components/ui/card';
import { cn } from '../../../../../../../../lib/utils';
import { formatLabel, StatusBadge } from '../../pipeMillReportUtils';
import {
  AdjudicationPanel,
  BendDispositionPanel,
  ChemicalFormulaRow,
  ChemistryResultRow,
  FlatteningDispositionPanel,
  HydrostaticDispositionPanel,
  ItemActionButtons,
  KeyValueRows,
  MiniStatusDot,
  PipeLocationDiagram,
  ReportMetadataRail,
  ResultRow,
  SectionHead,
  StatCard,
  TensileResultRow,
} from './shared/components';
import {
  AUT_DETAIL,
  BEND_DETAIL,
  CHEMICAL_DETAIL,
  CVN_DETAIL,
  DIMENSIONAL_DETAIL,
  HARDNESS_DETAIL,
  HYDROSTATIC_DETAIL,
  STANDARD_REPORT_DETAILS,
} from './shared/sampleData';
import {
  average,
  formatDisplayDate,
  getStandardReportSummaryStats,
  standardLimitText,
  standardScaleMax,
  standardScaleMin,
  statusNoteClassName,
} from './shared/format';
import {
  createSampleMeasurement,
  isSampleDataDeleted,
} from './shared/measurements';
import {
  getProjectLimitField,
  getProjectLimitMax,
  getProjectLimitMin,
  getProjectLimitRows,
  getProjectLimitText,
} from './shared/limits';
import type {
  ChemicalElementDisplay,
  FullReportType,
  ProjectLimitFieldOption,
  ReportDefinition,
  ReportMeasurementItem,
  ReportMetadataEntry,
  ReportSummary,
  StandardCheck,
} from './shared/types';
import type { BaseReportDetailProps } from './shared/reportProps';

export const BendReportDetail: React.FC<{
  heatNumber: string;
  metadata?: ReportMetadataEntry;
  projectLimitFields?: ProjectLimitFieldOption[];
  frequency?: string;
  measurements?: ReportMeasurementItem[];
  deletedMeasurementIds?: string[];
  onAddMeasurement?: (reportType: FullReportType) => void;
  onEditMeasurement?: (measurement: ReportMeasurementItem) => void;
  onDeleteMeasurement?: (measurement: ReportMeasurementItem) => void;
}> = ({ heatNumber, metadata, projectLimitFields = [], frequency, measurements = [], deletedMeasurementIds = [], onAddMeasurement, onEditMeasurement, onDeleteMeasurement }) => {
  const [activeBendId, setActiveBendId] = useState<string | null>(null);
  const bendMeasurements = measurements.filter((measurement) => measurement.reportType === 'BEND');
  const effectiveFrequency = metadata?.frequency || frequency || 'per_lot';
  const projectLimitRows = getProjectLimitRows(projectLimitFields, [
    ['No project limits', 'Configure project limits to show live values', true],
  ]);
  const specimens = [
    { id: 'B1', bendType: 'face_bend', angleDeg: 180, crackLengthMm: 0.0, fracture: false, status: 'PASS' },
    { id: 'B2', bendType: 'root_bend', angleDeg: 180, crackLengthMm: 1.4, fracture: false, status: 'PASS' },
  ].filter((item) => !isSampleDataDeleted(deletedMeasurementIds, `bend-${item.id}`)).map((item) => ({
    ...item,
    measurement: createSampleMeasurement('BEND', `bend-${item.id}`, 'Bend specimen', item.angleDeg, 'deg', 'Angle >= 180 deg; crack <= 3 mm; no fracture', item.status, {
      fieldPath: 'bend_specimen',
      bendSpecimenId: item.id,
      bendType: item.bendType,
      bendAngleDeg: String(item.angleDeg),
      bendCrackLengthMm: item.crackLengthMm.toFixed(1),
      bendFracture: item.fracture ? 'yes' : 'no',
      bendWallThicknessMm: '22.2',
      bendMultiplier: '6',
      bendRequiredFormerMm: '133.2',
      bendMillFormerMm: '133.2',
    }),
  })).concat([
    ...bendMeasurements.map((measurement) => ({
      id: measurement.bendSpecimenId || measurement.id,
      bendType: measurement.bendType || 'face_bend',
      angleDeg: Number(measurement.bendAngleDeg) || 0,
      crackLengthMm: Number(measurement.bendCrackLengthMm) || 0,
      fracture: measurement.bendFracture === 'yes',
      status: measurement.status,
      measurement,
    })),
  ]);
  const activeSpecimen = specimens.find((specimen) => specimen.id === activeBendId) || specimens[0];
  useEffect(() => {
    if (!specimens.length) {
      if (activeBendId) setActiveBendId(null);
      return;
    }
    if (!activeBendId || !specimens.some((specimen) => specimen.id === activeBendId)) {
      setActiveBendId(specimens[0].id);
    }
  }, [activeBendId, specimens]);
  const activeBendMeasurement = activeSpecimen?.measurement;
  const wallThickness = Number(activeBendMeasurement?.bendWallThicknessMm || 22.2);
  const multiplier = Number(activeBendMeasurement?.bendMultiplier || 6);
  const requiredFormer = wallThickness * multiplier;
  const millFormer = Number(activeBendMeasurement?.bendMillFormerMm || requiredFormer);

  const renderBendCard = (specimen: typeof specimens[number]) => (
    <button
      key={specimen.id}
      type="button"
      onClick={() => setActiveBendId(specimen.id)}
      className={cn('rounded-lg border bg-[#F2F4F7] p-4 text-left shadow-sm hover:border-[#D8DDE5] hover:shadow-md', activeSpecimen?.id === specimen.id ? 'border-blueprint-blue bg-[#EEF0F3]' : 'border-[#E2E5EA]')}
    >
      <div className="flex items-start justify-between gap-3">
        <div>
          <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{specimen.bendType.replace('_', ' ')}</div>
          <div className="mt-1 font-mono text-[12px] font-semibold text-blueprint-blue">{specimen.id}</div>
        </div>
        <StatusBadge status={specimen.status} />
      </div>
      <svg viewBox="0 0 220 140" className="mt-2 h-[140px] w-full">
        <circle cx="110" cy="100" r="40" fill="#D9DEE6" stroke="#464646" strokeWidth="1.5" />
        <path
          d="M 20 100 L 70 100 A 40 40 0 1 1 150 100 L 200 100"
          fill="none"
          stroke={specimen.bendType === 'face_bend' ? '#00273F' : '#9D5A54'}
          strokeWidth="6"
          strokeLinecap="round"
        />
        {specimen.crackLengthMm > 0 && (
          <g>
            <circle cx="110" cy="60" r="3" fill="#C4001F" />
            <text x="120" y="58" fontFamily="monospace" fontSize="9" fill="#C4001F">{specimen.crackLengthMm.toFixed(1)} mm</text>
          </g>
        )}
      </svg>
      <div className="mt-2 flex items-end gap-6">
        <div>
          <div className="text-[10px] text-heritage-grey">Angle</div>
          <div className="font-mono text-[16px] font-semibold text-blueprint-blue">{specimen.angleDeg} deg</div>
        </div>
        <div>
          <div className="text-[10px] text-heritage-grey">Crack</div>
          <div className={cn('font-mono text-[16px] font-semibold', specimen.crackLengthMm > 3 ? 'text-readable-red' : 'text-blueprint-blue')}>{specimen.crackLengthMm.toFixed(1)} mm</div>
        </div>
        <div className="ml-auto text-right">
          <div className="text-[10px] text-heritage-grey">Fracture</div>
          <div className="font-mono text-[16px] font-semibold text-blueprint-blue">{specimen.fracture ? 'Yes' : 'No'}</div>
        </div>
      </div>
    </button>
  );

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_340px]">
      <main className="space-y-5 p-6">
        <div className="flex flex-wrap items-end justify-between gap-3">
          <div>
            <h3 className="font-heading text-[16px] font-semibold text-blueprint-blue">Two specimens - face and root bend</h3>
            <div className="mt-1 text-[12px] text-heritage-grey">Each is bent 180deg around a mandrel - cracks &gt; 3 mm fail</div>
          </div>
          <Button type="button" size="sm" onClick={() => onAddMeasurement?.('BEND')} className="bg-hero-red text-white hover:bg-hero-red-60">
            <Plus className="mr-2 h-4 w-4" />
            Add Specimen
          </Button>
        </div>

        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {specimens.map(renderBendCard)}
        </div>

        <Card className="border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Mandrel sizing" subtitle={activeSpecimen ? `Selected specimen ${activeSpecimen.id} - ${activeSpecimen.bendType.replace('_', ' ')}` : 'Select a specimen row'} />
            <div className="grid grid-cols-2 gap-4 p-4 md:grid-cols-4">
              {[
                ['Wall thickness', `${wallThickness.toFixed(1)} mm`, ''],
                ['Multiplier (n)', `${multiplier} x`, 'WT > 12.7 mm'],
                ['Required former', `${requiredFormer.toFixed(1)} mm`, 'D = n x WT'],
                ['Mill used', `${millFormer.toFixed(1)} mm`, 'matches required'],
              ].map(([label, value, sub], index) => (
                <div key={label}>
                  <div className="text-[10px] font-bold uppercase tracking-wider text-heritage-grey">{label}</div>
                  <div className={cn('mt-1 font-heading text-[22px] font-semibold', index === 3 ? 'text-galvanizing-green' : 'text-blueprint-blue')}>{value}</div>
                  {sub && <div className="mt-1 text-[10px] text-heritage-grey">{sub}</div>}
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        <Card className="overflow-hidden border-[#E2E5EA]">
          <CardContent className="p-0">
            <SectionHead title="Specimens" />
            <table className="w-full text-left">
              <thead className="border-b border-[#E2E5EA] bg-page-bg text-[10px] font-bold uppercase tracking-wider text-heritage-grey">
                <tr>
                  <th className="px-4 py-3">Specimen</th>
                  <th className="px-4 py-3">Type</th>
                  <th className="px-4 py-3 text-right">Bend angle</th>
                  <th className="px-4 py-3 text-right">Crack length</th>
                  <th className="px-4 py-3">Fracture</th>
                  <th className="px-4 py-3">Status</th>
                  <th className="px-4 py-3 text-right">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-[#EEF0F3] text-[12px] text-text-grey">
                {specimens.map((item) => (
                  <tr key={item.id} onClick={() => setActiveBendId(item.id)} className={cn('cursor-pointer hover:bg-page-bg', activeSpecimen?.id === item.id && 'bg-[#E7F3FF]')}>
                    <td className="px-4 py-3 font-mono font-semibold">{item.id}</td>
                    <td className="px-4 py-3">{item.bendType.replace('_', ' ')}</td>
                    <td className="px-4 py-3 text-right font-mono">{item.angleDeg} deg</td>
                    <td className="px-4 py-3 text-right font-mono">{item.crackLengthMm.toFixed(1)} mm</td>
                    <td className="px-4 py-3">{item.fracture ? 'Yes' : 'No'}</td>
                    <td className="px-4 py-3"><StatusBadge status={item.status} /></td>
                    <td className="px-4 py-3 text-right">
                      {item.measurement && (
                        <ItemActionButtons measurement={item.measurement} onEditMeasurement={onEditMeasurement} onDeleteMeasurement={onDeleteMeasurement} />
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </main>

      <ReportMetadataRail
        rows={[
          ['Report ID', metadata?.reportId || '-', true],
          ['Performed at', formatDisplayDate(metadata?.performedAt), true],
          ['Heat', heatNumber, true],
          ['Lab', metadata?.lab || '-'],
          ['Technician', metadata?.technician || '-'],
          ['Source', metadata?.source ? formatLabel(metadata.source) : '-'],
          ['Source file', metadata?.sourceFile || '-', true],
        ]}
        extraTitle="Limits In Effect"
        extraRows={projectLimitRows}
      >
        <div className="mt-5 text-[10px] font-bold uppercase tracking-wider text-heritage-grey">Frequency / Scope</div>
        <Card className="mt-3 border-[#E2E5EA]">
          <CardContent className="p-4">
            <KeyValueRows
              rows={[
                ['Frequency', effectiveFrequency, true],
                ['Heat number', heatNumber || '-', true],
                ['Specimens', `${bendMeasurements.length} saved`, true],
              ]}
            />
          </CardContent>
        </Card>
        <AdjudicationPanel />
      </ReportMetadataRail>
    </div>
  );
};
